/**
 * @fileoverview Supabase Edge Functionsとの通信を担当するAPIクライアントモジュール。
 * 各関数はPromiseを返し、非同期処理を容易にします。
 */

import { Constants, supabaseClient } from "./constants";
import type { AppError, ErrorResponse } from "./types/api-error";
import type { ApiModule, ApiPayload, ApiResponse } from "./types/api.d";
import {
  QuestionEditData,
  UpdateQuestionRequest,
  UpdateQuestionResponse,
} from "./types/edit-question.d";
import { ExamData } from "./types/exam.d";
import { Question } from "./types/question.d";

export const Api = {
  /**
   * Supabase Edge Functionを呼び出す共通プライベート関数。
   * 認証トークンを自動的にヘッダーに付与します。
   * @private
   * @param {string} functionName - 呼び出すEdge Functionの名前。
   * @param {'GET' | 'POST' | 'PATCH'} [method='GET'] - HTTPメソッド。
   * @param {ApiPayload | null} [payload=null] - POST/PATCHリクエストで送信するデータ。
   * @returns {Promise<any>} Functionからのレスポンスボディ。
   * @throws {AppError} API呼び出しが失敗した場合、またはレスポンスがエラーを示した場合。
   */
  _call: async function <T>(
    functionName: string,
    method: string = "GET",
    payload: ApiPayload | null = null
  ): Promise<T> {
    const url = `https://${Constants.APP_CONFIG.SUPABASE_URL}/functions/v1/${functionName}`;

    // 現在のセッションからアクセストークンを取得
    const {
      data: { session },
    } = await supabaseClient.auth.getSession();
    const accessToken = session ? session.access_token : null;

    if (!accessToken) {
      console.warn(`[API] No access token found for call to ${functionName}`);
    } else {
      console.log(`[API] Token found for call to ${functionName}`);
    }

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      apikey: Constants.APP_CONFIG.SUPABASE_ANON_KEY as string, // 匿名キーは必須
    };

    // ログインしている場合はAuthorizationヘッダーを追加
    if (accessToken) {
      (headers as Record<string, string>)["Authorization"] =
        `Bearer ${accessToken}`;
    }

    const options: RequestInit = {
      method: method,
      headers: headers,
    };

    if (payload) {
      options.body = JSON.stringify(payload);
    }

    try {
      const response = await fetch(url, options);
      const responseBody: unknown = await response.json();

      if (!response.ok) {
        // エラー詳細をコンソールに出力 (デバッグ用)
        console.error(`[API Error] ${functionName}:`, responseBody);

        // responseBodyがオブジェクトでerrorプロパティを持つかチェック
        const errorMessage =
          responseBody &&
          typeof responseBody === "object" &&
          "error" in responseBody &&
          (responseBody as ErrorResponse).error &&
          typeof (responseBody as ErrorResponse).error === "string"
            ? (responseBody as ErrorResponse).error
            : response.statusText;

        // サーバーからのエラーレスポンスを整形して投げる
        const appError: AppError = new Error(errorMessage);
        appError.isAppError = true;
        appError.status = response.status;
        if (response.status === 401) {
          appError.message =
            "認証の有効期限が切れました。再度ログインしてください。";
        }
        throw appError;
      }
      return responseBody as T;
    } catch (error) {
      console.error(`API call to '${functionName}' failed:`, error);
      throw error;
    }
  },

  /**
   * 公開されているすべての試験のリストを取得します。
   * @returns {Promise<ExamData[]>} 試験情報の配列。
   */
  getExamNames: function (): Promise<ExamData[]> {
    return this._call<ExamData[]>("get-exam-names", "GET");
  },

  /**
   * 指定された条件でクイズの問題セットを取得します。
   * @param {number} examId - 試験のID。
   * @param {number} quizLength - 取得する問題数。
   * @param {string} algorithm - 使用する問題抽出アルゴリズム。
   * @returns {Promise<{questions: Question[]}>} 問題データのオブジェクト。
   */
  getQuizData: function (
    examId: number,
    quizLength: number,
    algorithm: string,
    area_ids: number[] | null
  ): Promise<{ questions: Question[] }> {
    const payload: ApiPayload = { examId, quizLength, algorithm, area_ids };
    return this._call<{ questions: Question[] }>(
      "get-quiz-data",
      "POST",
      payload
    );
  },

  /**
   * ユーザーの1問分の解答をサーバーに記録します。
   * @param {ApiPayload} payload - 解答データ。
   * @param {string} payload.questionId - 解答した問題のID。
   * @param {number} payload.selectedOptionId - 選択した選択肢のID。
   * @param {number} payload.answerTimeMs - 解答にかかった時間(ミリ秒)。
   * @param {number} [payload.recallTimeMs] - (フラッシュカードモード)思い出しにかかった時間(ミリ秒)。
   * @param {string} payload.quizMode - 現在のクイズモード。
   * @param {string} payload.displayModeUsed - 実際に使用された表示モード。
   * @param {string} [payload.sessionId] - 現在のクイズセッションID。
   * @returns {Promise<ApiResponse>} サーバーからの成功レスポンス。
   */
  recordSingleAnswer: function (payload: ApiPayload): Promise<ApiResponse> {
    return this._call<ApiResponse>("record-single-answer", "POST", payload);
  },

  /**
   * 指定された試験の学習履歴をリセットします。
   * @param {string} vendor - 試験の実施団体名。
   * @param {string} examName - 試験名。
   * @returns {Promise<ApiResponse>} サーバーからの成功レスポンス。
   */
  resetExamHistory: function (
    vendor: string,
    examName: string
  ): Promise<ApiResponse> {
    const payload: ApiPayload = { vendor, examName };
    return this._call<ApiResponse>("reset-exam-history", "POST", payload);
  },

  /**
   * 編集のために問題データを取得します。
   * @param {string} questionId - 取得する問題のID。
   * @returns {Promise<QuestionEditData>} 問題編集データ。
   */
  getQuestionDataForEdit: function (
    questionId: string
  ): Promise<QuestionEditData> {
    return this._call<QuestionEditData>(
      `update-question-data?id=${questionId}`,
      "GET"
    );
  },

  /**
   * 問題データを更新します。
   * @param {string} questionId - 更新する問題のID。
   * @param {UpdateQuestionRequest} data - 更新するデータ。
   * @returns {Promise<UpdateQuestionResponse>} 更新レスポンス。
   */
  updateQuestionData: function (
    questionId: string,
    data: UpdateQuestionRequest
  ): Promise<UpdateQuestionResponse> {
    return this._call<UpdateQuestionResponse>(
      `update-question-data?id=${questionId}`,
      "POST",
      data as unknown as ApiPayload
    );
  },

  /**
   * 指定された試験の領域リストを取得します（キャッシュ用）。
   * @param {number} examId - 試験ID。
   * @returns {Promise<{availableAreas: {id: number, name: string, questionCount: number}[]}>} 領域リスト。
   */
  getExamAreas: function (examId: number): Promise<{
    availableAreas: { id: number; name: string; questionCount: number }[];
  }> {
    return this._call<{
      availableAreas: { id: number; name: string; questionCount: number }[];
    }>(`update-question-data?exam_id=${examId}`, "GET");
  },

  /**
   * ログインユーザーの編集権限を確認します。
   * @returns {Promise<boolean>} 権限があればtrue。
   */
  checkUserPermission: async function (): Promise<boolean> {
    // 'questions.editor' ロールを持っているか確認
    const { data, error } = await supabaseClient.rpc("check_user_role", {
      required_role: "questions.editor",
    });
    if (error) {
      console.error("Permission check failed:", error);
      return false;
    }
    return !!data;
  },
} as ApiModule & {
  getQuestionDataForEdit(questionId: string): Promise<QuestionEditData>;
  updateQuestionData(
    questionId: string,
    data: UpdateQuestionRequest
  ): Promise<UpdateQuestionResponse>;
  getExamAreas(examId: number): Promise<{
    availableAreas: { id: number; name: string; questionCount: number }[];
  }>;
  checkUserPermission(): Promise<boolean>;
};
